import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth-store'
import { Pencil, Trash2, Plus, Lock, Navigation, Eye, EyeOff, GripVertical } from 'lucide-react'
import { usePages, useCreatePage, useDeletePage } from '@/hooks/use-pages'
import {
  useNavConfig,
  useUpdateNavConfig,
  useUpdateFooterConfig,
  DEFAULT_NAV_LINKS,
  DEFAULT_FOOTER_LINKS,
} from '@/hooks/use-nav-config'
import { BlockEditor } from '@/components/block-editor/block-editor'
import {
  useHomepageSections,
  useUpdateHomepageSections,
  type BlockConfig,
} from '@/hooks/use-homepage'
import { triggerRevalidation } from '@/lib/utils'
import type { NavLinkConfig, NavConfig, FooterConfig } from '@/hooks/use-nav-config'
import { useSubscription } from '@/hooks/use-subscription'
import { FeatureGate } from '@/components/feature-gate'

type PagesTab = 'homepage' | 'navigasi' | 'sistem' | 'kustom'
type NavSubTab = 'navbar' | 'footer'

// ── Helpers (Navigation) ──────────────────────────────────────────────────────

const PREDEFINED_NAV_IDS = ['about', 'members', 'music', 'events', 'shop']
const PREDEFINED_FOOTER_IDS = ['about', 'members', 'music', 'events', 'shop', 'cheki']

// Feature key required to show each predefined nav/footer link.
// Links without an entry are always visible (e.g. 'about').
const NAV_FEATURE_GATE: Record<string, string> = {
  members: 'members',
  events:  'events',
  music:   'music',
  shop:    'shop',
  cheki:   'cheki_form',
}

function mergeWithDefaults(
  saved: NavLinkConfig[] | undefined | null,
  defaults: NavLinkConfig[]
): NavLinkConfig[] {
  if (!saved || saved.length === 0) return defaults
  const savedIds = new Set(saved.map((l) => l.id))
  const missing = defaults.filter((d) => !savedIds.has(d.id))
  return [...saved, ...missing]
}

// ── Homepage Tab ───────────────────────────────────────────────────────────────

function HomepageTab() {
  const { data: initialBlocks = [], isLoading } = useHomepageSections()
  const updateBlocks = useUpdateHomepageSections()
  const [saved, setSaved] = useState(false)

  function handleSave(blocks: BlockConfig[]) {
    updateBlocks.mutate(blocks, {
      onSuccess: async () => {
        setSaved(true)
        setTimeout(() => setSaved(false), 3000)
        await triggerRevalidation('tenant-theme')
      },
    })
  }

  if (isLoading) return <p className="text-muted-foreground">Memuat...</p>

  return (
    <BlockEditor
      pageTitle="Homepage"
      pageDescription="Atur block yang ditampilkan di halaman utama publik, beserta urutan dan konfigurasinya."
      initialBlocks={initialBlocks}
      onSave={handleSave}
      isSaving={updateBlocks.isPending}
      saveError={updateBlocks.error as Error | null}
      saved={saved}
      listenToRouterState
    />
  )
}

// ── Navigation Tab ────────────────────────────────────────────────────────────

function NavigationTab() {
  const { data, isLoading } = useNavConfig()
  const updateNav = useUpdateNavConfig()
  const updateFooter = useUpdateFooterConfig()
  const { data: subscription, isSuccess: subLoaded } = useSubscription()
  const planFeatureKeys = new Set(
    (subscription?.plans?.plan_features ?? []).map((f) => f.feature_key)
  )

  const [activeSubTab, setActiveSubTab] = useState<NavSubTab>('navbar')
  const [navLinks, setNavLinks] = useState<NavLinkConfig[]>([])
  const [showCheki, setShowCheki] = useState(true)
  const [navSaved, setNavSaved] = useState(false)
  const [footerLinks, setFooterLinks] = useState<NavLinkConfig[]>([])
  const [footerSaved, setFooterSaved] = useState(false)

  useEffect(() => {
    if (data) {
      setNavLinks(mergeWithDefaults(data.nav_config?.links, DEFAULT_NAV_LINKS))
      setShowCheki(data.nav_config?.show_cheki_button ?? true)
      setFooterLinks(mergeWithDefaults(data.footer_config?.links, DEFAULT_FOOTER_LINKS))
    }
  }, [data])

  function updateNavLink(id: string, patch: Partial<NavLinkConfig>) {
    setNavLinks((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)))
  }

  function addCustomNavLink() {
    const id = `custom-${Date.now()}`
    setNavLinks((prev) => [...prev, { id, href: '', label: '', enabled: true, external: true }])
  }

  function removeCustomNavLink(id: string) {
    setNavLinks((prev) => prev.filter((l) => l.id !== id))
  }

  function handleSaveNav() {
    const config: NavConfig = { links: navLinks, show_cheki_button: showCheki }
    updateNav.mutate(config, {
      onSuccess: () => {
        setNavSaved(true)
        setTimeout(() => setNavSaved(false), 3000)
      },
    })
  }

  function updateFooterLink(id: string, patch: Partial<NavLinkConfig>) {
    setFooterLinks((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)))
  }

  function handleSaveFooter() {
    const config: FooterConfig = { links: footerLinks }
    updateFooter.mutate(config, {
      onSuccess: () => {
        setFooterSaved(true)
        setTimeout(() => setFooterSaved(false), 3000)
      },
    })
  }

  if (isLoading) return <p className="text-muted-foreground">Memuat konfigurasi navigasi...</p>

  const predefinedNavLinks = navLinks.filter((l) => {
    if (!PREDEFINED_NAV_IDS.includes(l.id)) return false
    const required = NAV_FEATURE_GATE[l.id]
    return !required || !subLoaded || planFeatureKeys.has(required)
  })
  const customNavLinks = navLinks.filter((l) => !PREDEFINED_NAV_IDS.includes(l.id))
  const predefinedFooterLinks = footerLinks.filter((l) => {
    if (!PREDEFINED_FOOTER_IDS.includes(l.id)) return false
    const required = NAV_FEATURE_GATE[l.id]
    return !required || !subLoaded || planFeatureKeys.has(required)
  })

  return (
    <div className="space-y-4">
      {/* Sub-tabs: Navbar | Footer */}
      <div className="flex gap-1 rounded-lg bg-muted p-1 w-fit">
        {(['navbar', 'footer'] as NavSubTab[]).map((sub) => (
          <button
            key={sub}
            type="button"
            onClick={() => setActiveSubTab(sub)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              activeSubTab === sub
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {sub === 'navbar' ? 'Navbar' : 'Footer'}
          </button>
        ))}
      </div>

      {/* Navbar */}
      {activeSubTab === 'navbar' && (
        <section className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-6 space-y-3">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Link Halaman
            </h3>
            {predefinedNavLinks.map((link) => (
              <div key={link.id} className="flex items-center gap-3">
                <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                <input
                  type="checkbox"
                  checked={link.enabled}
                  onChange={(e) => updateNavLink(link.id, { enabled: e.target.checked })}
                  className="h-4 w-4 accent-primary shrink-0"
                />
                <input
                  type="text"
                  value={link.label}
                  onChange={(e) => updateNavLink(link.id, { label: e.target.value })}
                  maxLength={30}
                  className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Label"
                />
                <span className="text-xs text-muted-foreground shrink-0 w-24">{link.href}</span>
              </div>
            ))}
          </div>

          {(!subLoaded || planFeatureKeys.has('custom_pages')) && (
          <div className="rounded-xl border border-border bg-card p-6 space-y-3">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Link Kustom (Eksternal)
            </h3>
            {customNavLinks.length === 0 && (
              <p className="text-sm text-muted-foreground italic">Belum ada link kustom.</p>
            )}
            {customNavLinks.map((link) => (
              <div key={link.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={link.enabled}
                  onChange={(e) => updateNavLink(link.id, { enabled: e.target.checked })}
                  className="h-4 w-4 accent-primary shrink-0"
                />
                <input
                  type="text"
                  value={link.label}
                  onChange={(e) => updateNavLink(link.id, { label: e.target.value })}
                  maxLength={30}
                  className="w-28 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Label"
                />
                <input
                  type="url"
                  value={link.href}
                  onChange={(e) => updateNavLink(link.id, { href: e.target.value })}
                  className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="https://..."
                />
                <button
                  type="button"
                  onClick={() => removeCustomNavLink(link.id)}
                  className="p-1.5 rounded-md text-destructive hover:bg-destructive/10 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addCustomNavLink}
              className="flex items-center gap-2 text-sm text-primary hover:underline"
            >
              <Plus className="h-4 w-4" />
              Tambah Link Kustom
            </button>
          </div>
          )}

          {(!subLoaded || planFeatureKeys.has('cheki_form')) && (
            <div className="rounded-xl border border-border bg-card p-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showCheki}
                  onChange={(e) => setShowCheki(e.target.checked)}
                  className="h-4 w-4 accent-primary"
                />
                <span className="text-sm font-medium">Tampilkan tombol Cheki di navbar</span>
              </label>
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={handleSaveNav}
              disabled={updateNav.isPending}
              className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {updateNav.isPending ? 'Menyimpan...' : 'Simpan Navbar'}
            </button>
            {navSaved && <p className="text-sm text-green-600">Navbar berhasil disimpan!</p>}
            {updateNav.error && (
              <p className="text-sm text-destructive">
                {(updateNav.error as Error).message ?? 'Terjadi kesalahan.'}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Footer */}
      {activeSubTab === 'footer' && (
        <section className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-6 space-y-3">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Link Halaman
            </h3>
            {predefinedFooterLinks.map((link) => (
              <div key={link.id} className="flex items-center gap-3">
                <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                <input
                  type="checkbox"
                  checked={link.enabled}
                  onChange={(e) => updateFooterLink(link.id, { enabled: e.target.checked })}
                  className="h-4 w-4 accent-primary shrink-0"
                />
                <input
                  type="text"
                  value={link.label}
                  onChange={(e) => updateFooterLink(link.id, { label: e.target.value })}
                  maxLength={30}
                  className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Label"
                />
                <span className="text-xs text-muted-foreground shrink-0 w-24">{link.href}</span>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleSaveFooter}
              disabled={updateFooter.isPending}
              className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {updateFooter.isPending ? 'Menyimpan...' : 'Simpan Footer'}
            </button>
            {footerSaved && <p className="text-sm text-green-600">Footer berhasil disimpan!</p>}
            {updateFooter.error && (
              <p className="text-sm text-destructive">
                {(updateFooter.error as Error).message ?? 'Terjadi kesalahan.'}
              </p>
            )}
          </div>
        </section>
      )}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function PagesManagerPage() {
  const { data: pages = [], isLoading } = usePages()
  const createPage = useCreatePage()
  const deletePage = useDeletePage()
  const navigate = useNavigate()
  const tenant = useAuthStore((s) => s.currentTenant)
  const { data: navConfigData } = useNavConfig()
  const updateNavConfig = useUpdateNavConfig()
  const { data: subscription, isSuccess: subLoaded } = useSubscription()
  const planFeatureKeys = new Set(
    (subscription?.plans?.plan_features ?? []).map((f) => f.feature_key)
  )

  const [activeTab, setActiveTab] = useState<PagesTab>('homepage')
  const [showCreate, setShowCreate] = useState(false)
  const [newSlug, setNewSlug] = useState('')
  const [newTitle, setNewTitle] = useState('')
  const [addToNavbar, setAddToNavbar] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  function handleCreate() {
    if (!newSlug.trim() || !newTitle.trim()) return
    createPage.mutate(
      { slug: newSlug, title: newTitle },
      {
        onSuccess: (page) => {
          if (addToNavbar) {
            const currentLinks = navConfigData?.nav_config?.links ?? DEFAULT_NAV_LINKS
            const newLink = { id: page.slug, href: `/${page.slug}`, label: page.title, enabled: true }
            updateNavConfig.mutate({
              links: [...currentLinks, newLink],
              show_cheki_button: navConfigData?.nav_config?.show_cheki_button ?? true,
            })
          }
          setShowCreate(false)
          setNewSlug('')
          setNewTitle('')
          setAddToNavbar(false)
          navigate(`/pages/${page.slug}`)
        },
      }
    )
  }

  function handleDelete(slug: string) {
    deletePage.mutate(slug, {
      onSuccess: () => setDeleteConfirm(null),
    })
  }

  function getNavState(slug: string) {
    const links = navConfigData?.nav_config?.links ?? DEFAULT_NAV_LINKS
    const link = links.find((l) => l.id === slug)
    if (!link) return 'not-in-nav'
    return link.enabled ? 'visible' : 'hidden'
  }

  function handleNavToggle(slug: string, title: string, action: 'add' | 'show' | 'hide') {
    const currentLinks: NavLinkConfig[] = navConfigData?.nav_config?.links ?? DEFAULT_NAV_LINKS
    let updatedLinks: NavLinkConfig[]

    if (action === 'add') {
      updatedLinks = [...currentLinks, { id: slug, href: `/${slug}`, label: title, enabled: true }]
    } else if (action === 'show') {
      updatedLinks = currentLinks.map((l) => l.id === slug ? { ...l, enabled: true } : l)
    } else {
      updatedLinks = currentLinks.map((l) => l.id === slug ? { ...l, enabled: false } : l)
    }

    updateNavConfig.mutate({
      links: updatedLinks,
      show_cheki_button: navConfigData?.nav_config?.show_cheki_button ?? true,
    })
  }

  const systemPages = pages
    .filter((p) => p.is_system)
    .filter((p) => {
      const required = NAV_FEATURE_GATE[p.slug]
      return !required || !subLoaded || planFeatureKeys.has(required)
    })
  const customPages = pages.filter((p) => !p.is_system)
  const memberProfilePage = pages.find((p) => p.slug === 'member-profile') ?? null

  const TABS: { key: PagesTab; label: string }[] = [
    { key: 'homepage',  label: 'Homepage' },
    { key: 'sistem',    label: 'Halaman Sistem' },
    { key: 'navigasi',  label: 'Navigasi' },
    { key: 'kustom',    label: 'Halaman Kustom' },
  ]

  return (
    <div className="max-w-2xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Kelola Halaman</h1>
          <p className="text-muted-foreground">
            Kustomisasi konten setiap halaman di situs publik.
          </p>
        </div>
        {activeTab === 'kustom' && (!subLoaded || planFeatureKeys.has('custom_pages')) && (
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Buat Halaman Baru
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => { setActiveTab(key); setShowCreate(false) }}
            className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === key
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab: Homepage */}
      {activeTab === 'homepage' && <HomepageTab />}

      {/* Tab: Navigasi */}
      {activeTab === 'navigasi' && <NavigationTab />}

      {/* Tab: Halaman Sistem */}
      {activeTab === 'sistem' && (
        <section className="space-y-3">
          {isLoading ? (
            <p className="text-muted-foreground text-sm">Memuat halaman...</p>
          ) : (
            <>
              {/* Dynamic system pages (member-profile has its own pinned entry) */}
              {systemPages
                .filter((p) => p.slug !== 'member-profile')
                .map((page) => (
                  <div
                    key={page.slug}
                    className="flex items-center gap-4 rounded-xl border border-border bg-card p-4"
                  >
                    <Lock className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{page.title}</p>
                      <p className="text-xs text-muted-foreground">/{page.slug} · {page.sections.length} block</p>
                    </div>
                    <button
                      onClick={() => navigate(`/pages/${page.slug}`)}
                      className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </button>
                  </div>
                ))}

              {/* Pinned: Profil Member — always visible when members feature is enabled */}
              {(!subLoaded || planFeatureKeys.has('members')) && (
                <div className="flex items-center gap-4 rounded-xl border border-border bg-card p-4">
                  <Lock className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">Profil Member</p>
                    <p className="text-xs text-muted-foreground">
                      {memberProfilePage
                        ? `/member-profile · ${memberProfilePage.sections.length} block`
                        : 'Belum diinisialisasi'}
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/pages/member-profile')}
                    className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    {memberProfilePage ? 'Edit' : 'Inisialisasi'}
                  </button>
                </div>
              )}

              {/* Empty state: only when there's nothing at all */}
              {systemPages.filter((p) => p.slug !== 'member-profile').length === 0 &&
                subLoaded && !planFeatureKeys.has('members') && (
                <p className="text-sm text-muted-foreground italic">
                  Tidak ada halaman sistem. Pastikan sudah menjalankan SQL migration.
                </p>
              )}
            </>
          )}
        </section>
      )}

      {/* Tab: Halaman Kustom */}
      {activeTab === 'kustom' && (
        <FeatureGate feature="custom_pages">
        <div className="space-y-6">
          {showCreate && (
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <h2 className="font-semibold">Halaman Baru</h2>
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Slug URL</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground shrink-0">
                      {tenant?.slug}.oneidol.id/
                    </span>
                    <input
                      type="text"
                      value={newSlug}
                      onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      placeholder="about"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Hanya huruf kecil, angka, dan tanda hubung (-).</p>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Judul Halaman</label>
                  <input
                    type="text"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="About Us"
                  />
                </div>
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={addToNavbar}
                    onChange={(e) => setAddToNavbar(e.target.checked)}
                    className="h-4 w-4 rounded border-input accent-primary"
                  />
                  <span className="text-sm">Tambahkan ke navbar</span>
                </label>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleCreate}
                  disabled={!newSlug.trim() || !newTitle.trim() || createPage.isPending}
                  className="rounded-md bg-primary px-5 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {createPage.isPending ? 'Membuat...' : 'Buat Halaman'}
                </button>
                <button
                  onClick={() => { setShowCreate(false); setNewSlug(''); setNewTitle(''); setAddToNavbar(false) }}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  Batal
                </button>
              </div>
              {createPage.error && (
                <p className="text-sm text-destructive">{(createPage.error as Error).message}</p>
              )}
            </div>
          )}

          <section className="space-y-3">
            {isLoading ? (
              <p className="text-muted-foreground text-sm">Memuat halaman...</p>
            ) : customPages.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">Belum ada halaman kustom.</p>
            ) : customPages.map((page) => {
              const navState = getNavState(page.slug)
              return (
                <div
                  key={page.slug}
                  className="flex items-center gap-4 rounded-xl border border-border bg-card p-4"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{page.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-xs text-muted-foreground">/{page.slug} · {page.sections.length} block</p>
                      {navState === 'visible' && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary font-medium">
                          <Navigation className="h-2.5 w-2.5" />
                          Navbar
                        </span>
                      )}
                      {navState === 'hidden' && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground font-medium">
                          <EyeOff className="h-2.5 w-2.5" />
                          Tersembunyi
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {navState === 'not-in-nav' && (
                      <button
                        onClick={() => handleNavToggle(page.slug, page.title, 'add')}
                        disabled={updateNavConfig.isPending}
                        className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
                      >
                        <Navigation className="h-3.5 w-3.5" />
                        + Navbar
                      </button>
                    )}
                    {navState === 'visible' && (
                      <button
                        onClick={() => handleNavToggle(page.slug, page.title, 'hide')}
                        disabled={updateNavConfig.isPending}
                        className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:border-destructive hover:text-destructive transition-colors disabled:opacity-50"
                      >
                        <EyeOff className="h-3.5 w-3.5" />
                        Sembunyikan
                      </button>
                    )}
                    {navState === 'hidden' && (
                      <button
                        onClick={() => handleNavToggle(page.slug, page.title, 'show')}
                        disabled={updateNavConfig.isPending}
                        className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        Tampilkan
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/pages/${page.slug}`)}
                      className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </button>
                    {deleteConfirm === page.slug ? (
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleDelete(page.slug)}
                          disabled={deletePage.isPending}
                          className="rounded-md bg-destructive px-3 py-1.5 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
                        >
                          {deletePage.isPending ? '...' : 'Ya, hapus'}
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="text-sm text-muted-foreground hover:text-foreground"
                        >
                          Batal
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(page.slug)}
                        className="rounded-md p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </section>
        </div>
        </FeatureGate>
      )}
    </div>
  )
}
